// src/pages/Contact.jsx
import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import toast from 'react-hot-toast';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const { error: insertError } = await supabase
        .from('contact_messages')
        .insert([
          {
            name: formData.name.trim(),
            email: formData.email.trim(),
            subject: formData.subject.trim() || null,
            message: formData.message.trim(),
          },
        ]);

      if (insertError) throw insertError;

      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      toast.success('Message sent! We will get back to you soon.');
    } catch (err) {
      console.error('Contact form error:', err);
      setError(err.message || 'Failed to send message. Please try again.');
      toast.error('Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-4" style={{ paddingTop: '80px' }}>
      <Container>
        {/* Header */}
        <div className="text-center mb-5">
          <h1 className="display-4 fw-bold">Contact Us</h1>
          <p className="text-muted">
            We'd love to hear from you. Reach out and we'll respond shortly.
          </p>
        </div>

        <Row className="g-4">
          {/* Left: Contact Info & Map */}
          <Col lg={5}>
            <div className="d-flex flex-column gap-4">
              <h2 className="h4 fw-bold">Get in touch</h2>
              <p className="text-muted">
                Have a question about your resume? Our team is here to help.
              </p>

              <div className="d-flex flex-column gap-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle p-2"
                    style={{ backgroundColor: 'rgba(26, 158, 150, 0.12)', color: '#1A9E96' }}
                  >
                    <Mail size={20} />
                  </div>
                  <div>
                    <div className="fw-semibold">Email</div>
                    <div className="text-muted small">supports@fastglobalresume.com</div>
                  </div>
                </div>

                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle p-2"
                    style={{ backgroundColor: 'rgba(26, 158, 150, 0.12)', color: '#1A9E96' }}
                  >
                    <Phone size={20} />
                  </div>
                  <div>
                    <div className="fw-semibold">Phone</div>
                    <div className="text-muted small">+91 9935921436</div>
                  </div>
                </div>

                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle p-2"
                    style={{ backgroundColor: 'rgba(26, 158, 150, 0.12)', color: '#1A9E96' }}
                  >
                    <MapPin size={20} />
                  </div>
                  <div>
                    <div className="fw-semibold">Office</div>
                    <div className="text-muted small">
                      165/126, Opp, Mahila College, Aminabad, Lucknow 226018, U.P. India
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-xl overflow-hidden border border-secondary-subtle">
                <iframe
                  src="https://www.openstreetmap.org/export/embed.html?bbox=80.91486454010011%2C26.846463027347852%2C80.92591524124147%2C26.851210878330466&layer=mapnik&marker=26.848837%2C80.920390"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                />
              </div>
            </div>
          </Col>

          {/* Right: Contact Form */}
          <Col lg={7}>
            <Card className="shadow-sm border-0">
              <Card.Body className="p-4">
                {submitted ? (
                  /* Success state */
                  <div className="text-center py-5">
                    <CheckCircle size={64} style={{ color: '#1A9E96' }} className="mb-3" />
                    <h4 className="fw-bold">Message Sent!</h4>
                    <p className="text-muted">
                      Thanks for reaching out. We'll get back to you within 24 hours.
                    </p>
                    <Button
                      onClick={() => setSubmitted(false)}
                      className="btn-hero-primary mt-3"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  /* Form state */
                  <>
                    <h3 className="fw-bold mb-3">Send us a message</h3>
                    <p className="text-muted small">
                      Fill out the form and we'll get back to you.
                    </p>

                    {error && (
                      <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        {error}
                      </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="mb-3">
                        <Form.Label>Full Name *</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Email Address *</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Subject</Form.Label>
                        <Form.Control
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="How can we help?"
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Message *</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="message"
                          required
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Write your message here..."
                        />
                      </Form.Group>

                      <Button
                        type="submit"
                        disabled={submitting}
                        className="btn-hero-primary w-100 d-flex align-items-center justify-content-center gap-2"
                      >
                        {submitting ? (
                          <>
                            <Spinner as="span" animation="border" size="sm" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send size={18} /> Send Message
                          </>
                        )}
                      </Button>
                    </Form>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Contact;